import { useMemo } from 'react'
import { Disclosure } from '@headlessui/react'
import { ChevronDownIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import ViolationRow from './ViolationRow'
import type { ComplianceViolation } from '@/types'

type GroupKey = 'critical' | 'high' | 'medium' | 'passing'

interface GroupedPoliciesDisplayProps {
  policies: ComplianceViolation[]
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
  frameworkCode?: string  // For contextual framework reference display
  /** When set, that group renders open on initial mount (deep-link entry). */
  initialOpenKey?: GroupKey
}

interface PolicyGroup {
  key: GroupKey
  policies: ComplianceViolation[]
  defaultOpen: boolean
}

// Editorial severity palette — aligns with BestPracticeAnalyzerTab and PriorityQueue.
// Critical uses a solid badge (matches the §06 violations sub-nav badge); the rest are tinted pills.
const GROUP_META: Record<GroupKey, { label: string; countClasses: string; labelClasses: string }> = {
  critical: { label: 'Critical (SHALL)', countClasses: 'bg-[#A03434] text-white', labelClasses: 'text-[#A03434]' },
  high: { label: 'High (SHOULD)', countClasses: 'bg-[#FDF3DF] text-[#8C6B22]', labelClasses: 'text-[#8C6B22]' },
  medium: { label: 'Medium (MAY)', countClasses: 'bg-[#E8F0F6] text-[#4F86A8]', labelClasses: 'text-[#4F86A8]' },
  passing: { label: 'Passing', countClasses: 'bg-[#E2F1E8] text-[#1F6B43]', labelClasses: 'text-[#1F6B43]' },
}

/**
 * Groups policies by status (Pass) and criticality (SHALL/SHOULD/MAY) into expandable sections.
 * Uses single-pass O(n) loop for grouping instead of multiple filter calls.
 * Passing section collapsed by default; first non-empty non-passing section expanded.
 */
export default function GroupedPoliciesDisplay({
  policies,
  onAddWaiver,
  onEditWaiver,
  onDeleteWaiver,
  frameworkCode,
  initialOpenKey,
}: GroupedPoliciesDisplayProps) {
  // Group policies by status (Pass) and criticality (SHALL/SHOULD/MAY)
  // Performance optimization: Single pass through array instead of 4 filter calls
  const groups = useMemo<PolicyGroup[]>(() => {
    const passing: ComplianceViolation[] = []
    const critical: ComplianceViolation[] = []
    const high: ComplianceViolation[] = []
    const medium: ComplianceViolation[] = []

    // Single iteration - O(n) instead of O(4n)
    for (const p of policies) {
      if (p.status === 'Pass') {
        passing.push(p)
      } else {
        switch (p.criticality) {
          case 'SHALL':
            critical.push(p)
            break
          case 'SHOULD':
            high.push(p)
            break
          case 'MAY':
            medium.push(p)
            break
        }
      }
    }

    const buckets: Record<GroupKey, ComplianceViolation[]> = { critical, high, medium, passing }
    return (Object.keys(buckets) as GroupKey[]).map((key) => ({
      key,
      policies: buckets[key],
      defaultOpen: key === initialOpenKey,
    }))
  }, [policies, initialOpenKey])

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const meta = GROUP_META[group.key]
        return (
        <Disclosure key={group.key} defaultOpen={group.defaultOpen}>
          {({ open }) => (
            <div className="overflow-hidden rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]">
              <Disclosure.Button className="flex w-full items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-[color:var(--color-cream,#FBF8F2)]">
                <div className="flex items-center gap-3">
                  {group.key === 'passing' && (
                    <CheckCircleIcon className="h-4 w-4 text-[#1F6B43]" />
                  )}
                  <span
                    className={`text-[11px] font-bold uppercase tracking-[0.18em] ${meta.labelClasses}`}
                  >
                    {meta.label}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${meta.countClasses}`}
                  >
                    {group.policies.length}
                  </span>
                </div>
                <ChevronDownIcon
                  className={`h-4 w-4 text-[color:var(--color-faint,#9CA3AF)] transition-transform duration-200 ${
                    open ? 'rotate-180' : ''
                  }`}
                />
              </Disclosure.Button>

              <Disclosure.Panel className="border-t border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-4">
                {group.policies.length === 0 ? (
                  <p className="py-4 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
                    No policies in this category.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {group.policies.map((policy) => (
                      <ViolationRow
                        key={policy.id}
                        violation={policy}
                        onAddWaiver={group.key !== 'passing' ? onAddWaiver : undefined}
                        onEditWaiver={group.key !== 'passing' ? onEditWaiver : undefined}
                        onDeleteWaiver={group.key !== 'passing' ? onDeleteWaiver : undefined}
                        frameworkCode={frameworkCode}
                      />
                    ))}
                  </div>
                )}
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
        )
      })}
    </div>
  )
}
